<template>
  <div class="container pl-0 ml-0">
    <div class="row justify-content-start" v-if="initialized">
      <!-- date -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Date Range</h6>
        <DatePicker
          @update="dateUpdate"
          :minDate="selectOldestCompanyStartDate([activeCompany._id])"
          :dateRange="filteredDateRange"
        />
      </div>

      <!-- location -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Locations</h6>
        <GroupingFilter
          :nonGroupMembers="locations"
          nonGroupMemberLabel="Locations"
          memberType="location"
          @update="groupingUpdate"
          :init="{
            nonGroupMemberIds: this.filteredLocations,
            groupIds: this.filteredGroups
          }"
        />
      </div>

      <!-- competitors -->
      <div class="dropdown col-auto">
        <h6 class="header-pretitle mt-3">Competitors</h6>
        <GenericMultiSelect
          :options="selectCompetitorOptions"
          @update="competitorUpdate"
          :init="initialSelectedCompetitors"
          :placeholder="'All competitors'"
          labelKey="label"
          trackByKey="value"
          emitKey="value"
        />
      </div>

      <!-- filter button -->
      <div class="col-auto dropdown">
        <h6 class="header-pretitle mt-3">&nbsp;</h6>
        <button class="btn btn-primary" @click="formatAndApplyFilters">Apply</button>
      </div>
    </div>
  </div>
</template>

<script>
import GroupingFilter from '@/components/Modules/Filters/GroupingFilterV2'
import DatePicker from '@/components/Modules/Filters/DatePicker'
import GenericMultiSelect from '@/components/Modules/Filters/GenericMultiSelect'
import { createNamespacedHelpers } from 'vuex'

const LocationModule = createNamespacedHelpers('location')
const CompanyModule = createNamespacedHelpers('company')
const GroupModule = createNamespacedHelpers('group')
const CompetitionModule = createNamespacedHelpers('competition')

export default {
  name: 'CompetitionSummaryFilters',
  components: {
    GroupingFilter,
    DatePicker,
    GenericMultiSelect
  },
  data: () => ({
    initialized: false,
    initialLoadComplete: false,
    filteredDateRange: null,
    filteredGroups: null,
    filteredGroupMembers: null,
    filteredLocations: null,
    filteredDisabledLocations: null,
    filteredCompetitors: null
  }),
  mounted() {
    this.initializeData()
  },
  computed: {
    ...LocationModule.mapGetters(['selectActiveLocations', 'selectDisabledLocations']),
    ...CompanyModule.mapGetters(['activeCompany', 'selectOldestCompanyStartDate']),
    ...CompetitionModule.mapGetters(['selectCompetitorOptions', 'selectCompetitors']),
    locations() {
      return this.selectActiveLocations.filter((l) => !l.hideFromReports)
    },
    initialSelectedCompetitors() {
      if (!this.filteredCompetitors || !this.selectCompetitorOptions.length) {
        return []
      }
      return this.selectCompetitorOptions.filter((option) =>
        this.filteredCompetitors.includes(option.value)
      )
    }
  },
  methods: {
    ...GroupModule.mapActions(['selectGroupLocationIds']),
    ...CompetitionModule.mapActions(['applyFilters', 'setLastSummaryUrl']),
    async initializeData() {
      this.filteredDateRange = this.$route.query.dateRange
      this.filteredLocations = this.$route.query.locs
      this.filteredGroups = this.$route.query.groups
      this.filteredDisabledLocations = this.$route.query.disLocs
      this.filteredCompetitors = this.$route.query.comps

      if (this.filteredCompetitors && !Array.isArray(this.filteredCompetitors)) {
        this.filteredCompetitors = [this.filteredCompetitors]
      }
      if (this.filteredGroups && !Array.isArray(this.filteredGroups)) {
        this.filteredGroups = [this.filteredGroups]
      }
      if (this.filteredLocations && !Array.isArray(this.filteredLocations)) {
        this.filteredLocations = [this.filteredLocations]
      }
      if (this.filteredDisabledLocations && !Array.isArray(this.filteredDisabledLocations)) {
        this.filteredDisabledLocations = [this.filteredDisabledLocations]
      }

      if (!(await this.accessPermitted())) {
        this.$router.replace({ query: {} })
        this.$emit('refresh')
        this.$notify({ text: 'You lack permissions needed to view this link', type: 'error' })
      } else {
        this.initialized = true
      }
    },
    async accessPermitted() {
      let permitted = true
      if (this.filteredLocations?.length) {
        permitted = this.filteredLocations.every((l) =>
          this.selectActiveLocations.find((aL) => aL._id === l)
        )
      }
      return permitted
    },
    groupingUpdate(val) {
      this.filteredGroups = val && val.groupIds && val.groupIds.length ? val.groupIds : null
      this.filteredGroupMembers =
        val && val.memberIds && val.memberIds.length ? val.memberIds : null
      this.filteredLocations =
        val && val.locationIds && val.locationIds.length ? val.locationIds : null
    },
    dateUpdate(val) {
      this.filteredDateRange = val
      if (!this.initialLoadComplete) {
        this.formatAndApplyFilters()
        this.initialLoadComplete = true
      }
    },
    competitorUpdate(val) {
      this.filteredCompetitors = val
    },
    async formatAndApplyFilters() {
      let locationsToFilter = [...new Set([...(this.filteredLocations || [])])]
      this.applyFilters({
        locations: locationsToFilter.length ? locationsToFilter : null,
        groups: this.filteredGroups?.length ? this.filteredGroups : null,
        dateRange:
          this.filteredDateRange && Object.keys(this.filteredDateRange).length
            ? this.filteredDateRange
            : null,
        competitors: this.filteredCompetitors
      })
      this.$emit('filtersApplied')
      this.$router
        .replace({
          path: this.$route.path,
          query: {
            dateRange: this.filteredDateRange,
            ...(this.filteredGroups && this.filteredGroups.length
              ? { groups: this.filteredGroups }
              : {
                  ...(this.filteredLocations?.length ? { locs: this.filteredLocations } : {})
                }),
            ...(this.filteredCompetitors?.length ? { comps: this.filteredCompetitors } : {})
          }
        })
        .catch((err) => {
          if (err.name !== 'NavigationDuplicated') throw err
        })
      this.setLastSummaryUrl(this.$route.fullPath)
    }
  }
}
</script>
